import {
  revokeApplicationCache,
  reconcileApplicationSearchCache,
} from "@client/offline/revokeApplicationCache";
import {
  dehydrate,
  hydrate,
  QueryClient,
  QueryCache,
  QueryClientProvider,
} from "@tanstack/react-query";
import { type ReactNode, useEffect, useRef, useState } from "react";
// The deferred wrapper, NOT @sentry/react: a static SDK import here would
// pull all of Sentry into the startup bundle (TRPCProvider is in the entry's
// import closure). The wrapper queues calls until the SDK chunk loads.
import { captureMessage } from "./telemetry/sentry";
import { buildAuthHeaders, trpc, trpcUrl } from "./trpc";
import { createTrpcLinks } from "./trpcLinks";
import { useImpersonationRefValue } from "./ImperativeState";
import { isIPadCapacitor } from "@client/lib/isIPad";
import {
  ARTIFACT_CACHE_MAX_AGE_MS,
  clearArtifactCache,
  readArtifactCache,
  writeArtifactCache,
} from "@client/offline/queryClientPersister";
import {
  shouldPersistArtifactQuery,
  shouldEvictApplicationQuery,
} from "@client/offline/artifactQueryPersistence";
import {
  initialRenderGateOpen,
  resolveArtifactRestore,
} from "@client/offline/artifactCacheGating";
import { roundAggregateSchemaHash } from "@lib/roundSchemaHash";
import { getIsOnline } from "@client/hooks/useNetworkStatus";
import { shouldRetryArtifactQuery } from "@client/offline/queryRetryPolicy";
import { useCurrentUser } from "@client/hooks/use-current-user";

// iPad offline cache (#2 AI artifacts): persist the read-only artifact queries
// to IndexedDB (via react-query's own dehydrate/hydrate) and serve them
// offline. Gated to the iPad shell so desktop/web and iPhone are byte-identical.
// Read-only data, so no write-queue.
const IPAD_OFFLINE = isIPadCapacitor();
// ARTIFACT_CACHE_MAX_AGE_MS (persisted-snapshot expiry) is owned by
// queryClientPersister so the hydrate-time check here and the merge there share
// one source of truth.
// In-memory GC retention. MUST stay under setTimeout's 32-bit ceiling
// (2^31-1 ≈ 24.8 days): React Query schedules GC via setTimeout(fn, gcTime),
// and any delay above that ceiling silently clamps to 1ms — which would evict
// hydrated artifact queries ~immediately, before any component observes them,
// defeating the offline cache. Cross-launch durability is the persisted
// IndexedDB snapshot (ARTIFACT_CACHE_MAX_AGE_MS), not this in-memory window.
const ARTIFACT_GC_TIME_MS = 1000 * 60 * 60 * 24 * 24; // 24 days, < 2^31-1 ms
const PERSIST_THROTTLE_MS = 2000;

const shouldDehydrate = (query: {
  state: { status: string };
  queryKey: unknown;
}) =>
  query.state.status === "success" &&
  shouldPersistArtifactQuery(query.queryKey);

/**
 * Mirrors the artifact query cache to IndexedDB and rehydrates it on launch.
 * Gates rendering on the (fast) initial hydrate so queries don't fire — and
 * fail under `offlineFirst` — before the persisted cache is restored.
 */
function ArtifactCacheGate({
  queryClient,
  children,
}: {
  queryClient: QueryClient;
  children: ReactNode;
}) {
  // Two gates (SP-2592): `hydrated` releases render — paint only waits for
  // the restore when the cache is the data source (offline; see
  // initialRenderGateOpen). `restoreSettled` releases the persist loop —
  // persisting before the restore lands can't destroy the snapshot (writes
  // merge per query), but it would burn multi-MB writes on near-empty state.
  const [hydrated, setHydrated] = useState(() =>
    initialRenderGateOpen(getIsOnline()),
  );
  const [restoreSettled, setRestoreSettled] = useState(false);

  // The underlying (logged-in) user. AI artifacts are anonymized per viewer, so
  // a persisted snapshot is scoped to this id. Offline-stable via
  // useCurrentUser's cached-identity fallback; deduped with the auth gate's
  // query, so this adds no request.
  const { data: currentUser } = useCurrentUser();
  const userId = currentUser?.userId ?? null;
  const userIdRef = useRef<string | null>(userId);
  userIdRef.current = userId;

  // Hydrate (or purge) the persisted snapshot against the signed-in user. We
  // hydrate ONLY once the identity is known AND owns the snapshot — never
  // optimistically on an unknown identity. Otherwise, online before
  // getCurrentUser resolves, a previous account's anonymized artifacts (relogin
  // without sign-out) would be hydrated and readable in the window before a
  // later purge ran — evaluators must never see each other's data.
  //   - Offline: useCurrentUser yields the cached identity synchronously, so the
  //     match check happens before first paint and no query fires uncached.
  //   - Online: identity is initially null → we skip hydration and unblock
  //     immediately (queries fetch from the network); once it resolves we
  //     hydrate if it matches (hydrate respects dataUpdatedAt, so it never
  //     clobbers a fresher just-fetched query) or purge on a mismatch.
  // Also handles an in-session account change (A→B): the new id isn't `handled`
  // yet, so the snapshot (still A's) is detected as a mismatch and purged.
  const handledUserRef = useRef<string | null>(null);
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      if (userId != null && handledUserRef.current !== userId) {
        const cached = await readArtifactCache();

        // Only mark this id handled once the read actually resolved uncancelled —
        // if the effect was cancelled mid-read (a fast identity flip), leave it
        // unmarked so the next render re-attempts rather than skipping hydration.
        if (!cancelled) {
          handledUserRef.current = userId;
          const action = resolveArtifactRestore({
            cached,
            userId,
            schemaHash: roundAggregateSchemaHash,
            maxAgeMs: ARTIFACT_CACHE_MAX_AGE_MS,
            now: Date.now(),
          });

          if (action === "hydrate" && cached != null) {
            hydrate(queryClient, cached.state);
          } else if (action === "purge") {
            // Snapshot belongs to a different account — purge in-memory +
            // persisted so it can never be served to this user. resetQueries,
            // not clear(): the gate can release render before identity
            // resolves, so components (and their queries) may already be
            // mounted here — clear() would detach those observers without
            // refetching and freeze every on-screen query (see the
            // impersonation-change effect below).
            void queryClient.resetQueries();
            void clearArtifactCache().catch((error: unknown) => {
              captureMessage(
                `Artifact-cache privacy clear failed: ${String(error)}`,
                { level: "error" },
              );
            });
          }
        }
      }

      if (!cancelled) {
        setHydrated(true);

        // Persisting stays closed until a restore attempt completed for a
        // KNOWN identity — an unknown identity has nothing to restore yet.
        if (userId != null) {
          setRestoreSettled(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userId, queryClient]);

  useEffect(() => {
    if (!restoreSettled) {
      return;
    }

    let timer: ReturnType<typeof setTimeout> | null = null;
    let initialTimer: ReturnType<typeof setTimeout> | null = null;

    const persist = () => {
      timer = null;

      // Never persist while offline: the artifact queries fail offline and drop
      // out of the dehydrated set, so writing would clobber the good online
      // snapshot with an empty one. Use getIsOnline() (not raw navigator.onLine)
      // so the force-offline seams agree with the rest of the offline stack.
      if (!getIsOnline()) {
        return;
      }

      // Can't scope the snapshot to a user yet — skip this tick rather than
      // persist an unattributed snapshot a different user could later hydrate.
      const uid = userIdRef.current;

      if (uid == null) {
        return;
      }

      void writeArtifactCache({
        buster: roundAggregateSchemaHash,
        userId: uid,
        timestamp: Date.now(),
        state: dehydrate(queryClient, {
          shouldDehydrateQuery: shouldDehydrate,
        }),
      });
    };

    const unsubscribe = queryClient.getQueryCache().subscribe(() => {
      if (timer == null) {
        timer = setTimeout(persist, PERSIST_THROTTLE_MS);
      }
    });
    // Initial pass: queries can settle before this subscription exists (render
    // no longer waits for the restore), and would otherwise only persist on a
    // later cache event. Safe in any order — writes merge per query.
    initialTimer = setTimeout(persist, PERSIST_THROTTLE_MS);

    return () => {
      unsubscribe();

      if (timer != null) {
        clearTimeout(timer);
      }

      if (initialTimer != null) {
        clearTimeout(initialTimer);
      }
    };
  }, [restoreSettled, queryClient]);

  // Blocks only on an offline iPad boot; desktop never mounts this.
  if (!hydrated) {
    return null;
  }

  return <>{children}</>;
}

export function TRPCProvider({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onSuccess: (data, query) => {
            void reconcileApplicationSearchCache(
              queryClient,
              query.queryKey,
              data,
              IPAD_OFFLINE,
            ).catch((error: unknown) => {
              captureMessage(
                `Application-cache reconciliation failed: ${String(error)}`,
                { level: "error" },
              );
            });
          },
          onError: (_error, query) => {
            // A subsequent offline retry must not revive data the server denied.
            if (shouldEvictApplicationQuery(query.queryKey, _error)) {
              void revokeApplicationCache(
                queryClient,
                query.queryKey,
                _error,
                IPAD_OFFLINE,
              ).catch((error: unknown) => {
                captureMessage(
                  `Application-cache eviction failed: ${String(error)}`,
                  { level: "error" },
                );
              });
            }
          },
        }),
        defaultOptions: {
          queries: {
            staleTime: 1000 * 60, // 1 minute
            refetchOnWindowFocus: false,
            // iPad only: don't pause queries while offline — attempt once, then
            // fall back to the (hydrated) cache. Desktop/web keep the default
            // "online" (pause offline), so their behavior is unchanged.
            ...(IPAD_OFFLINE
              ? {
                  networkMode: "offlineFirst" as const,
                  gcTime: ARTIFACT_GC_TIME_MS,
                  // Don't retry while offline — React Query's default 3-retry
                  // exponential backoff (~1+2+4s) otherwise delays the offline
                  // auth/boot fallback by ~7s on a cold launch (the dominant
                  // cost of offline open). Retrying a dead network is pointless;
                  // online keeps a normal couple of retries for transient blips.
                  // See queryRetryPolicy for the rationale + unit tests.
                  retry: (failureCount: number) =>
                    shouldRetryArtifactQuery({
                      failureCount,
                      isOnline: getIsOnline(),
                    }),
                }
              : {}),
          },
        },
      }),
  );

  // When impersonation changes, the effective user changes but artifact query
  // *keys* don't (impersonation rides on a header). Desktop just invalidates
  // (refetch). On the iPad we additionally CLEAR the in-memory + persisted
  // cache so the previous user's artifacts can never be served to the new
  // effective user offline (evaluators must not see each other's data).
  const viewAsUserId = useImpersonationRefValue();
  const prevRef = useRef(viewAsUserId);
  useEffect(() => {
    if (prevRef.current !== viewAsUserId) {
      prevRef.current = viewAsUserId;

      if (IPAD_OFFLINE) {
        // resetQueries, NOT clear(): both drop every cached payload (the
        // privacy requirement — evaluators must not see each other's data),
        // but clear() detaches mounted observers without refetching them, so
        // every active query on the page sat frozen with no data and no
        // request in flight — impersonating from the round-selection page
        // spun forever (Pete/Jaan, Aug 2026). resetQueries resets to initial
        // state AND refetches active queries as the new effective viewer.
        void queryClient.resetQueries();
        void clearArtifactCache().catch((error: unknown) => {
          // The clear already escalated to deleting the whole database and
          // STILL could not make the disk safe — surface it; the previous
          // effective viewer's artifacts may survive a force-quit.
          captureMessage(
            `Artifact-cache privacy clear failed: ${String(error)}`,
            { level: "error" },
          );
        });
        // The localStorage nav caches (homepage round list, per-round profiles)
        // need no clearing here: both are keyed by effective user
        // (OFFLINE_CACHE_KEYS.homepageData / userProfiles), so a switch reads a
        // different entry — no cross-user bleed, and each user's data stays
        // available offline. The identity caches (currentUser{,Id}) are the
        // *underlying* user and must persist to stay signed in offline.
      } else {
        void queryClient.invalidateQueries();
      }
    }
  }, [viewAsUserId, queryClient]);

  // splitLink stack: operations classified via CRITICAL_TRPC_OPTIONS get their
  // own unbatched request; everything else keeps the existing batching (see
  // trpcLinks.ts for the routing and the batch URL-length cap).
  const [trpcClient] = useState(() =>
    trpc.createClient({
      links: createTrpcLinks({ url: trpcUrl, headers: buildAuthHeaders }),
    }),
  );

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        {IPAD_OFFLINE ? (
          <ArtifactCacheGate queryClient={queryClient}>
            {children}
          </ArtifactCacheGate>
        ) : (
          children
        )}
      </QueryClientProvider>
    </trpc.Provider>
  );
}
